import { LocalStore } from '@/platform/localStore';
import type { ChildEdition, WorldPlacement, BadgeGrant, ArtworkRevision } from '@/contracts/family';
import type { Story, WorldSlotKind } from '@/contracts/content';
import { SyncService } from './sync';

/**
 * Personal editions, world placements and badges (spec §10.1, §10.3, §7 C15).
 *
 * Invariants held here:
 *  - An edition REFERENCES a story version and specific artwork revisions.
 *    It never mutates the published story (§10.1).
 *  - Editing a draft never silently changes an edition. The child is offered
 *    "Use this version" instead (§10.1).
 *  - Removing a world placement never deletes artwork (§7 C13).
 *  - A badge is granted once per eligible milestone, and never for
 *    popularity, payment or consecutive-day use (§7 C15).
 */

function now() {
  return new Date().toISOString();
}

export const EditionService = {
  async listForChild(account: string, childId: string): Promise<ChildEdition[]> {
    const all = await LocalStore.list<ChildEdition>('editions', account);
    return all.filter((e) => e.childId === childId).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },

  async get(account: string, editionId: string) {
    return LocalStore.get<ChildEdition>('editions', account, editionId);
  },

  /** The edition for this child + story version, created on first use. */
  async ensureForStory(account: string, childId: string, story: Story): Promise<ChildEdition> {
    const all = await EditionService.listForChild(account, childId);
    const existing = all.find(
      (e) => e.storyId === story.id && e.versionId === story.currentVersion.versionId,
    );
    if (existing) return existing;

    const edition: ChildEdition = {
      id: `ed_${crypto.randomUUID()}`,
      ownerUserId: account,
      childId,
      storyId: story.id,
      versionId: story.currentVersion.versionId,
      slots: {},
      coverRevisionId: null,
      createdAt: now(),
    };
    await LocalStore.put('editions', account, edition.id, edition);
    void SyncService.queue('editions', edition.id, { childId: edition.childId });
    return edition;
  },

  /**
   * Places a finished artwork revision into an approved page slot.
   * Rejects a template that is not approved for that page, so a picture can
   * never land on an unrelated page (§10.1).
   */
  async placeInSlot(
    account: string,
    edition: ChildEdition,
    story: Story,
    pageId: string,
    revision: ArtworkRevision,
  ): Promise<ChildEdition | { error: 'slot-not-approved' }> {
    const slot = story.currentVersion.editionSlots.find((s) => s.pageId === pageId);
    if (!slot) return { error: 'slot-not-approved' };
    if (!revision.templateId || !slot.acceptsTemplateIds.includes(revision.templateId)) {
      return { error: 'slot-not-approved' };
    }
    const next: ChildEdition = {
      ...edition,
      slots: { ...edition.slots, [pageId]: revision.id },
    };
    await LocalStore.put('editions', account, edition.id, next);
    void SyncService.queue('editions', edition.id, { childId: edition.childId });
    return next;
  },

  /** Finds the approved slot a revision belongs in, if any. */
  slotForRevision(story: Story, revision: ArtworkRevision) {
    if (!revision.templateId) return null;
    return (
      story.currentVersion.editionSlots.find((s) =>
        s.acceptsTemplateIds.includes(revision.templateId!),
      ) ?? null
    );
  },

  async setCover(account: string, edition: ChildEdition, revisionId: string | null) {
    const next: ChildEdition = { ...edition, coverRevisionId: revisionId };
    await LocalStore.put('editions', account, edition.id, next);
    void SyncService.queue('editions', edition.id, { childId: edition.childId });
    return next;
  },

  async clearSlot(account: string, edition: ChildEdition, pageId: string) {
    const slots = { ...edition.slots };
    delete slots[pageId];
    const next: ChildEdition = { ...edition, slots };
    await LocalStore.put('editions', account, edition.id, next);
    void SyncService.queue('editions', edition.id, { childId: edition.childId });
    return next;
  },
};

/* ------------------------------------------------------------------ *
 * My World (spec §7 C13, §10.3)
 * ------------------------------------------------------------------ */

/** Authored slots in the home scene. Positions are percentages of the stage. */
export interface WorldSlotDef {
  kind: WorldSlotKind;
  index: number;
  label: string;
  /** Placement box as percentages of the scene. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export const WORLD_SLOTS: WorldSlotDef[] = [
  { kind: 'wall-frame', index: 0, label: 'The left picture frame', x: 5, y: 10, w: 26, h: 26 },
  { kind: 'wall-frame', index: 1, label: 'The right picture frame', x: 5, y: 42, w: 26, h: 26 },
  { kind: 'window', index: 0, label: 'The window', x: 63, y: 12, w: 30, h: 34 },
  { kind: 'shelf', index: 0, label: 'The shelf', x: 36, y: 52, w: 22, h: 22 },
  { kind: 'garden', index: 0, label: 'The garden pot', x: 36, y: 76, w: 20, h: 18 },
];

export function slotKey(kind: WorldSlotKind, index: number) {
  return `${kind}:${index}`;
}

export const WorldService = {
  async listForChild(account: string, childId: string): Promise<WorldPlacement[]> {
    const all = await LocalStore.list<WorldPlacement>('placements', account);
    return all.filter((p) => p.childId === childId);
  },

  async place(
    account: string,
    childId: string,
    slot: WorldSlotDef,
    revision: ArtworkRevision,
    allowedSlots: WorldSlotKind[],
  ): Promise<WorldPlacement | { error: 'slot-not-eligible' }> {
    // A creation can only occupy a slot type its template supports (§10.3).
    if (!allowedSlots.includes(slot.kind)) return { error: 'slot-not-eligible' };

    const id = `place_${childId}_${slotKey(slot.kind, slot.index)}`;
    const placement: WorldPlacement = {
      id,
      ownerUserId: account,
      childId,
      slot: slot.kind,
      slotIndex: slot.index,
      artworkRevisionId: revision.id,
      transform: { x: 0, y: 0, scale: 1 },
      createdAt: now(),
    };
    await LocalStore.put('placements', account, id, placement);
    void SyncService.queue('placements', id, { childId: placement.childId });
    return placement;
  },

  /** Removing a placement leaves the artwork completely intact (§7 C13). */
  async remove(account: string, placementId: string) {
    await LocalStore.delete('placements', account, placementId);
  },

  async resetLayout(account: string, childId: string) {
    const mine = await WorldService.listForChild(account, childId);
    for (const p of mine) await LocalStore.delete('placements', account, p.id);
  },
};

/* ------------------------------------------------------------------ *
 * Badges (spec §7 C15, §14.2)
 * ------------------------------------------------------------------ */

export interface BadgeDef {
  id: string;
  name: string;
  description: string;
  emoji: string;
  /** Evaluated against participation and creation only. */
  test(stats: BadgeStats): boolean;
}

export interface BadgeStats {
  storiesFinished: number;
  picturesFinished: number;
  differentTemplatesUsed: number;
  editionsWithArtwork: number;
  worldPlacements: number;
  livingScenesViewed: number;
}

export const BADGES: BadgeDef[] = [
  {
    id: 'badge_first_story',
    name: 'First story finished',
    description: 'You read a whole story and explored it.',
    emoji: '📖',
    test: (s) => s.storiesFinished >= 1,
  },
  {
    id: 'badge_first_picture',
    name: 'First picture made',
    description: 'You finished a picture of your own.',
    emoji: '🎨',
    test: (s) => s.picturesFinished >= 1,
  },
  {
    id: 'badge_three_pictures',
    name: 'Three different pictures',
    description: 'You tried all three pictures from a story.',
    emoji: '🌈',
    test: (s) => s.differentTemplatesUsed >= 3,
  },
  {
    id: 'badge_own_book',
    name: 'Your own book',
    description: 'Your picture went into your own copy of a story.',
    emoji: '📚',
    test: (s) => s.editionsWithArtwork >= 1,
  },
  {
    id: 'badge_alive',
    name: 'Brought it to life',
    description: 'You watched your own picture move in the story.',
    emoji: '✨',
    test: (s) => s.livingScenesViewed >= 1,
  },
  {
    id: 'badge_decorator',
    name: 'Home decorator',
    description: 'You put something you made into your world.',
    emoji: '🏡',
    test: (s) => s.worldPlacements >= 1,
  },
];

export const BadgeService = {
  async granted(account: string, childId: string): Promise<BadgeGrant[]> {
    const all = await LocalStore.list<BadgeGrant>('meta', account);
    return (all as unknown[] as BadgeGrant[]).filter(
      (g) => g && typeof g === 'object' && 'badgeId' in g && g.childId === childId,
    );
  },

  /**
   * Grants any newly earned badges. Deterministic id per child+badge, so a
   * repeat evaluation can never double-grant (§14.2).
   */
  async evaluate(account: string, childId: string, stats: BadgeStats): Promise<BadgeGrant[]> {
    const fresh: BadgeGrant[] = [];
    for (const def of BADGES) {
      if (!def.test(stats)) continue;
      const id = `grant_${childId}_${def.id}`;
      const existing = await LocalStore.get<BadgeGrant>('meta', account, id);
      if (existing) continue;
      const grant: BadgeGrant = {
        id,
        ownerUserId: account,
        childId,
        badgeId: def.id,
        grantedAt: now(),
      };
      await LocalStore.put('meta', account, id, grant);
      fresh.push(grant);
    }
    return fresh;
  },

  async earnedIds(account: string, childId: string): Promise<string[]> {
    const out: string[] = [];
    for (const def of BADGES) {
      const g = await LocalStore.get<BadgeGrant>('meta', account, `grant_${childId}_${def.id}`);
      if (g) out.push(def.id);
    }
    return out;
  },
};
